const CardConverter = require('../Utilities/Converters/CardConverter.js');
const CardToData = require('../Utilities/Converters/CardToData.js');
const { TechnologyLookup, GetRequiredHappyFaces, GetConsumption } = require('../Utilities/Utilities.js');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const Leaders = require('../Constants/Leaders/Leaders');
const LeaderNames = require('../Constants/Names/LeaderNames');
const WonderNames = require('../Constants/Names/WonderNames');
const { LeaderDefinition, WonderDefinition } = require('../Constants/CardVersions');
const TerritoryEffects = require('../Constants/Territories/TerritoryEffects');
const { Bonuses, TurnFlags, Rules, HasRuleException, LogTrigger } = require('../Effects/Effects');
const { TacticArmies } = require('../Effects/Tactics');
const TacticConverter = require('../Utilities/Converters/TacticConverter');

// The card state of a player saved before there was cardState: the fields cards had of their own
const LegacyCardState = (data) => Object.fromEntries([
    [LeaderNames.Hippocrates, data.hippocratesTokens ? { tokens: data.hippocratesTokens } : null],
    [LeaderNames.Saladin, data.saladinChoice ? { choice: data.saladinChoice } : null],
    [LeaderNames.JuliusCaesar, data.caesarUsed ? { used: true } : null],
    [LeaderNames.CatherineTheGreat, data.catherineUsed ? { used: true } : null],
    [LeaderNames.PierreDeCoubertin, data.coubertinUsed ? { used: true } : null],
    [LeaderNames.IanFleming, data.flemingLastTarget !== undefined && data.flemingLastTarget !== null ? { lastTarget: data.flemingLastTarget } : null],
    [WonderNames.LouvreMuseum, data.louvreTokens ? { tokens: data.louvreTokens } : null],
].filter(([, state]) => state));

// Tokens every civilization starts with: 16 blue, and 25 yellow (18 in the bank, 1 unused worker, 6 on cards)
const StartingTokens = { blue: 16, yellow: 25 };

class PlayerState {
    constructor(playerStateData, gameState)
    {
        this.gameState = gameState;
        this.name = playerStateData.name;
        this.playerNumber = playerStateData.playerNumber;

        this.militaryHand = playerStateData.militaryHand.map((cardData) => CardConverter(cardData));
        this.civilHand = playerStateData.civilHand.map((cardData) => CardConverter(cardData));
        // Ages a leader has been taken from (one leader per age: picking it up counts, not putting it in play). Any
        // leader the player holds, in play or in hand, was picked up, so its age counts too.
        this.leaderAgesTaken = [...new Set([...(playerStateData.leaderAgesTaken ?? []),
            ...[playerStateData.leader, ...this.civilHand].filter(card => card?.type === 'Leader').map(card => card.age)])];
        this.workerPool = playerStateData.workerPool;
        this.civilActionsUsed = playerStateData.civilActionsUsed ?? 0;
        this.militaryActionsUsed = playerStateData.militaryActionsUsed ?? 0;

        this.technologies = playerStateData.technologies.map(tech => this.parseTech(tech));

        this.wonders = (playerStateData.wonders ?? []).map(cardData => CardConverter(cardData));
        this.wonderInProgress = playerStateData.wonderInProgress
            ? { card: CardConverter(playerStateData.wonderInProgress.card), stagesBuilt: playerStateData.wonderInProgress.stagesBuilt,
                ...(playerStateData.wonderInProgress.stagesBy ? { stagesBy: [...playerStateData.wonderInProgress.stagesBy] } : {}) }
            : null;
        this.leader = CardConverter(playerStateData.leader);
        this.currentTactic = playerStateData.currentTactic ?? null;
        this.exclusiveTactic = CardConverter(playerStateData.exclusiveTactic);
        // Tactics replaced while Sun Tzu was in play: still in the player's area, where no one can copy them, until the
        // first turn without him (see Moves makeTacticPublic)
        this.keptTactics = (playerStateData.keptTactics ?? []).map(cardData => CardConverter(cardData));
        // The tactic card the player is on (a card Sun Tzu went under is not like the others of its name), when known
        this.currentTacticId = playerStateData.currentTacticId ?? null;
        this.changedTacticThisTurn = playerStateData.changedTacticThisTurn ?? false;
        // Things done this turn that cards care about, e.g. a once-per-turn discount used, or the leader replaced
        this.turnFlags = [...(playerStateData.turnFlags ?? [])];
        // This turn only (cleared when it ends): amounts that pay for costs of a kind ({ source, kind, amount }, e.g.
        // Patriotism's resources for military units), bonuses to the player's numbers ({ source, stat, amount }), and
        // the ids of cards taken from the card row (they cannot be played until a later turn)
        this.turnPools = (playerStateData.turnPools ?? []).map(pool => ({ ...pool }));
        // How much of each card's pool (see Effects.AddResourcePool) has been used this turn, by card name
        this.turnPoolUsage = { ...(playerStateData.turnPoolUsage ?? {}) };
        // Discounts in place while an action card's own action is worked out (never saved; see Costs.WithCardDiscount)
        this.cardDiscounts = [];
        this.turnBonuses = (playerStateData.turnBonuses ?? []).map(bonus => ({ ...bonus }));
        this.takenThisTurn = [...(playerStateData.takenThisTurn ?? [])];
        // Happy faces added to completed wonders by other cards, for good: [{ wonderId, source }] (Homer, when replaced)
        this.wonderHappyFaces = (playerStateData.wonderHappyFaces ?? []).map(bonus => ({ ...bonus }));
        // Wonders turned face down by Ravages of Time (card ids): still wonders of their age, with no effect but 2 culture
        this.crumbledWonders = [...(playerStateData.crumbledWonders ?? [])];
        // Territories colonized, in the order they were (see Constants/Territories/TerritoryEffects.js)
        this.colonies = (playerStateData.colonies ?? []).map(cardData => CardConverter(cardData));
        // Gave up their next political action (International Agreement)
        this.skipNextPolitical = playerStateData.skipNextPolitical ?? false;
        // What cards keep track of for this player, by card name (see CardState), e.g.
        //   Hippocrates: { tokens }       yellow tokens above the government card (up to 3), one back into the bank
        //                                 at each new age; they stay after he leaves play
        //   Saladin: { choice }           his choice after the last action phase, kept until the next one: 'strength'
        //                                 (+2) or 'civil' (+1 civil action)
        //   Julius Caesar: { used }       his second political action has been used (once per game)
        //   Catherine the Great: { used } her political action has been used (once per game)
        //   Pierre de Coubertin: { used } his Olympic Games have been declared (once per game)
        //   Ian Fleming: { lastTarget }   whose military cards he looked at last turn (not them again this turn)
        //   Louvre Museum: { tokens }     blue tokens kept on it once completed (a wonder that keepsTokens)
        this.cardState = JSON.parse(JSON.stringify(playerStateData.cardState ?? LegacyCardState(playerStateData)));
        // Out of the game (see Moves/GameEnd.js)
        this.resigned = playerStateData.resigned ?? false;
        this.wonders.forEach(wonder => { wonder.crumbled = this.crumbledWonders.includes(wonder.id); });
        // Shown on the wonder's card: the happy face along its bottom, and the portrait of the leader who added it
        this.wonders.forEach(wonder => {
            wonder.addedHappyFaces = this.wonderHappyFaces
                .filter(bonus => bonus.wonderId === wonder.id)
                .map(bonus => ({ source: bonus.source, image: Leaders[bonus.source]?.image }));
        });

        this.science = playerStateData.science;
        this.culture = playerStateData.culture;
        this.government = playerStateData.government;
        // Blue and yellow tokens gained or lost other than through cards in play (events, aggressions, wars...):
        // { blue: [{ source, amount }], yellow: [...] }. The banks themselves are worked out (see blueBank).
        this.tokenChanges = {
            blue: [...(playerStateData.tokenChanges?.blue ?? [])],
            yellow: [...(playerStateData.tokenChanges?.yellow ?? [])],
        };

        this.currentFoodCalculationDelegate = [];
        this.currentResourceCalculationDelegate = [];

        this.foodProductionCalculationDelegate = [];
        this.resourceProductionCalculationDelegate = [];

        this.happyFaceCalculationDelegate = [];
        this.scienceProductionCalculationDelegate = [];
        this.cultureProductionCalculationDelegate = [];
        this.militaryCalculationDelegate = [];
        this.tacticCalculationDelegate = [];
        this.colonizationBonusCalculationDelegate = [];

        this.civilActionCalculationDelegate = [];
        this.militaryActionCalculationDelegate = [];
        this.urbanBuildingLimitCalculationDelegate = [];
        // Discontent workers not counted (Forbidden City)
        this.ignoredDiscontentCalculationDelegate = [];
        // Extra hand size beyond the civil / military action totals (e.g. Library of Alexandria)
        this.civilHandLimitCalculationDelegate = [];
        this.militaryHandLimitCalculationDelegate = [];
        // Changes to the costs of actions (see Effects.AddCostModifier and Moves/Costs.js)
        this.costModifierDelegate = [];
        // Tokens owned thanks to cards in play (e.g. Taj Mahal, civil technologies); lost with the card
        this.blueTokenCalculationDelegate = [];
        this.yellowTokenCalculationDelegate = [];
        // Reactions to things happening during the turn (see Effects.AddTrigger)
        this.triggerDelegate = [];
        // Rules cards in play let the player ignore (see Effects.AddRuleException)
        this.ruleExceptionDelegate = [];
        // Extra resources for some kinds of spending, from cards in play (see Effects.AddResourcePool)
        this.resourcePoolDelegate = [];
        // Strength added only in some situations (see Effects.AddSituationalStrength)
        this.situationalStrengthDelegate = [];

        this.foodProductionDelegate = [];
        this.resourceProductionDelegate = [];

        this.BuildDelegates();
    }

    // The version of a technology the expansion changed, in play in this game (its setup's; see Constants/CardVersions.js)
    TechnologyVersion(name) {
        return require('../Constants/CardVersions').TechnologyVersion(this.gameState?.setup, name);
    }

    parseTech(tech) {
        var techObject = TechnologyLookup(tech.Type).GetInstance(this.TechnologyVersion(tech.Type));
        techObject.contents = { ...tech.contents };
        return techObject;
    }

    // What a card keeps track of for this player (see cardState), to read and change: an object kept by the card's name
    CardState(name) {
        return this.cardState[name] ??= {};
    }

    // Blue tokens kept on completed wonders that keep them (the Louvre Museum's)
    TokensKeptOnWonders() {
        return this.wonders.filter(wonder => WonderDefinition(wonder)?.keepsTokens)
            .reduce((sum, wonder) => sum + (this.cardState[wonder.name]?.tokens ?? 0), 0);
    }

    // Serializable form; round-trips through the constructor
    toData() {
        return {
            name: this.name,
            playerNumber: this.playerNumber,
            militaryHand: this.militaryHand.map(CardToData),
            civilHand: this.civilHand.map(CardToData),
            workerPool: this.workerPool,
            civilActionsUsed: this.civilActionsUsed,
            militaryActionsUsed: this.militaryActionsUsed,
            technologies: this.technologies.map(tech => ({ Type: tech.name, contents: { ...tech.contents } })),
            wonders: this.wonders.map(CardToData),
            wonderInProgress: this.wonderInProgress
                ? { card: CardToData(this.wonderInProgress.card), stagesBuilt: this.wonderInProgress.stagesBuilt,
                    ...(this.wonderInProgress.stagesBy ? { stagesBy: [...this.wonderInProgress.stagesBy] } : {}) }
                : null,
            leader: CardToData(this.leader),
            leaderAgesTaken: this.leaderAgesTaken,
            currentTactic: this.currentTactic,
            exclusiveTactic: CardToData(this.exclusiveTactic),
            keptTactics: this.keptTactics.map(CardToData),
            currentTacticId: this.currentTacticId,
            changedTacticThisTurn: this.changedTacticThisTurn,
            turnFlags: [...this.turnFlags],
            turnPools: this.turnPools.map(pool => ({ ...pool })),
            turnPoolUsage: { ...this.turnPoolUsage },
            turnBonuses: this.turnBonuses.map(bonus => ({ ...bonus })),
            takenThisTurn: [...this.takenThisTurn],
            wonderHappyFaces: this.wonderHappyFaces.map(bonus => ({ ...bonus })),
            crumbledWonders: [...this.crumbledWonders],
            colonies: this.colonies.map(CardToData),
            skipNextPolitical: this.skipNextPolitical,
            resigned: this.resigned,
            // (only cards with something kept)
            cardState: JSON.parse(JSON.stringify(Object.fromEntries(Object.entries(this.cardState).filter(([, state]) => Object.keys(state).length > 0)))),
            science: this.science,
            culture: this.culture,
            government: this.government,
            tokenChanges: { blue: [...this.tokenChanges.blue], yellow: [...this.tokenChanges.yellow] },
        };
    }

    SumDelegate(delegateArray) {
        if (delegateArray.length === 0)
        {
            return 0;
        }
        var sum = 0;
        delegateArray.forEach(delegate => {
            sum += delegate.Action();
        });
        return sum < 0 ? 0 : sum;
    }

    // Higher Priority runs first (e.g. higher-level farms take blue tokens first when the bank runs short)
    ExecuteDelegate(delegateArray) {
        if (delegateArray.length === 0)
        {
            return;
        }
        [...delegateArray]
            .sort((a, b) => (b.Priority ?? 0) - (a.Priority ?? 0))
            .forEach(delegate => {
                delegate.Action();
            });
    }

    // Where a total comes from: one entry per producing card, with what other cards add to it
    // ({ source: "Movies", value: 8, from: [{ source: "Charles Chaplin", value: 4 }] }). Several contributions from the
    // same card, e.g. a fixed bonus and a computed one, are added together.
    GetBreakdown(delegateArray) {
        const byCard = new Map();
        delegateArray.forEach(delegate => {
            const value = delegate.Action();
            const name = (delegate.Through ?? delegate.Source)?.name ?? "Unknown";
            const entry = byCard.get(name) ?? { value: 0, from: new Map(), unnumbered: new Set() };
            entry.value += value;
            // (e.g. a tactic completed twice: "×2")
            const note = delegate.Note?.();
            if (note)
            {
                entry.note = note;
            }
            if (delegate.Through)
            {
                const from = delegate.Source?.name ?? "Unknown";
                entry.from.set(from, (entry.from.get(from) ?? 0) + value);
                // (a part shown by name only, e.g. "Jan Žižka (2 farms)" under a tactic)
                if (delegate.Unnumbered)
                {
                    entry.unnumbered.add(from);
                }
            }
            byCard.set(name, entry);
        });
        return [...byCard].map(([source, entry]) => ({
            source,
            value: entry.value,
            ...(entry.note ? { note: entry.note } : {}),
            from: [...entry.from].map(([from, value]) => ({ source: from, value, ...(entry.unnumbered.has(from) ? { unnumbered: true } : {}) })).filter(part => part.value !== 0),
        })).filter(entry => entry.value !== 0 || entry.from.length > 0);
    }

    // Wiring only: must be safe to run every time the state is rebuilt from data.
    // One-time effects belong in effects.onAcquire.
    BuildDelegates() {
        this.technologies.forEach(tech =>{
            if (tech.effects?.playerStateAltering != null)
            {
                tech.effects.playerStateAltering(this);
            }
        });
        //government
        this.governmentTechnology = TechnologyLookup(this.government).GetInstance(this.TechnologyVersion(this.government))
        if (this.governmentTechnology.effects?.playerStateAltering != null)
        {
            this.governmentTechnology.effects.playerStateAltering(this);
        }
        // The leader in play and completed wonders (see Constants/Leaders and Constants/Wonders): each card's printed
        // bonuses, then its other effects
        const applyCard = (definition, card) => {
            Bonuses(this, card, definition?.bonuses ?? {});
            definition?.effects?.(this, card);
        };
        if (this.leader)
        {
            applyCard(LeaderDefinition(this.leader), this.leader);
        }
        // A crumbled wonder loses all its effects and produces 2 culture instead
        this.wonders.forEach(wonder => wonder.crumbled
            ? this.cultureProductionCalculationDelegate.push({ Source: { name: `Crumbled ${wonder.name}`, type: 'Wonder' }, Action: () => 2 })
            : applyCard(WonderDefinition(wonder), wonder));
        // A happy face added to a wonder is the wonder's, with the card that added it noted
        this.wonderHappyFaces.forEach(bonus => {
            const wonder = this.wonders.find(w => w.id === bonus.wonderId);
            if (wonder)
            {
                this.happyFaceCalculationDelegate.push({ Source: { name: bonus.source }, Through: wonder, Action: () => 1, AddedTo: wonder });
            }
        });
        // Colonies: what each gives while held
        this.colonies.forEach(colony => Bonuses(this, colony, TerritoryEffects[colony.name]?.bonuses(colony) ?? {}));
        // This turn's bonuses, credited to the card that gave them. A military action for this turn (Patriotism) can be
        // spent like the others but is not part of the military action total (the hand limit, a revolution with
        // Robespierre, defending: see GetMilitaryActionCount)
        this.turnBonuses.forEach(bonus => bonus.stat === 'militaryActions'
            ? this.militaryActionCalculationDelegate.push({ Source: { name: bonus.source }, Action: () => bonus.amount, Temporary: true })
            : Bonuses(this, { name: bonus.source }, { [bonus.stat]: bonus.amount }));
        // The current tactic: strength for the armies the military units form (last, as cards in play can change how
        // armies are formed, e.g. Genghis Khan)
        const tactic = this.currentTactic && TacticConverter(this.currentTactic);
        if (tactic)
        {
            const armies = () => this.GetTacticArmies();
            this.militaryCalculationDelegate.push({
                Source: tactic,
                Action: () => armies().value - (armies().extraValue ?? 0),
                // Completed more than once: "×2"
                Note: () => { const count = armies().full + armies().obsolete; return count > 1 ? `×${count}` : null; },
            });
            const player = this;
            // (with units the cards add: Jan Žižka's farms, Marlene Dietrich's unit counted twice)
            (HasRuleException(this, Rules.FarmsAsUnits) || HasRuleException(this, Rules.DoubleOneUnit)) && this.militaryCalculationDelegate.push({
                Source: { get name() { const { extraBy, extraText } = player.GetTacticArmies(); return `${extraBy} (${extraText})`; } },
                Through: tactic,
                Unnumbered: true,
                Action: () => armies().extraValue ?? 0,
            });
        }
        //territories
    }

    AddTechnology(technology) {
        if (technology === null || this.HasTechnology(technology.name))
        {
            return;
        }
        this.technologies.push(technology);
        if (technology.effects?.onAcquire != null)
        {
            technology.effects.onAcquire(this);
        }
        if (technology.effects?.playerStateAltering != null)
        {
            technology.effects.playerStateAltering(this);
        }
    }

    HasTechnology = (name) => this.technologies.some(tech => tech.name === name);
    GetTechnology = (name) => this.technologies.find(tech => tech.name === name);

// Producing (the production phase, or an event): first the usual production, a token per worker on each farm or mine,
    // and what cards make particular cards produce (Bill Gates's labs, Transcontinental Railroad, Machu Picchu); then the
    // production bonuses, every other source of food or resources (Isabella of Castile, Suez Canal, pacts, Communism...),
    // added up and gained all at once (so three +1 can be one token on Coal), or lost when the total is negative
    ProduceFood = () => { this.ExecuteDelegate(this.foodProductionDelegate); this.ProduceBonus('food'); };
    ProduceResources = () => { this.ExecuteDelegate(this.resourceProductionDelegate); this.ProduceBonus('resources'); };

    // The production bonuses of food or resources (see ProduceFood): what the player's numbers count that no farm or mine
    // produces itself, nor a card through one
    GetProductionBonus = (stat) => (stat === 'food' ? this.foodProductionCalculationDelegate : this.resourceProductionCalculationDelegate)
        .filter(d => !d.Through && ![TechnologyTypes.Production.Farm, TechnologyTypes.Production.Mine].includes(d.Source?.type))
        .reduce((sum, d) => sum + d.Action(), 0);

    ProduceBonus = (stat) => {
        const bonus = this.GetProductionBonus(stat);
        if (bonus > 0)
        {
            stat === 'food' ? this.GainFood(bonus) : this.GainResources(bonus);
        } else if (bonus < 0) {
            stat === 'food' ? this.LoseFood(-bonus) : this.LoseResources(-bonus);
        }
    };

    GetResourceProduction = () => this.SumDelegate(this.resourceProductionCalculationDelegate)
    GetFoodProduction = () => this.SumDelegate(this.foodProductionCalculationDelegate);
    // What is produced less what the workers eat (shown as the food production; GetFoodProduction is what the cards make)
    GetNetFoodProduction = () => this.GetFoodProduction() - GetConsumption(this.yellowBank);
    GetCultureProduction = () => this.SumDelegate(this.cultureProductionCalculationDelegate);
    GetScienceProduction = () => this.SumDelegate(this.scienceProductionCalculationDelegate);

    GetFood = () => this.SumDelegate(this.currentFoodCalculationDelegate);
    GetResources = () => this.SumDelegate(this.currentResourceCalculationDelegate);

    GetMilitaryStrength = () => this.SumDelegate(this.militaryCalculationDelegate);
    // Happy faces count from 0 to 8
    GetHappyFaces = () => Math.min(8, this.SumDelegate(this.happyFaceCalculationDelegate));
    // Workers short of happy faces; an uprising happens when they outnumber the unused workers
    // (less those cards say to ignore, e.g. Forbidden City's 2)
    GetDiscontentWorkers = () => Math.max(0, GetRequiredHappyFaces(this.yellowBank) - this.GetHappyFaces() - this.SumDelegate(this.ignoredDiscontentCalculationDelegate));
    HasUprising = () => this.GetDiscontentWorkers() > this.workerPool;
    GetColonizationBonus = () => this.SumDelegate(this.colonizationBonusCalculationDelegate);

    // (the usual civil actions: not those for some actions only, e.g. Gutenberg's; see GetRestrictedCivilActions)
    GetCivilActionCount = () => this.SumDelegate(this.civilActionCalculationDelegate.filter(d => !d.Restricted));
    // (the total: not those for this turn only, e.g. Patriotism's; see GetTemporaryMilitaryActions)
    GetMilitaryActionCount = () => this.SumDelegate(this.militaryActionCalculationDelegate.filter(d => !d.Temporary));
    GetTemporaryMilitaryActions = () => this.SumDelegate(this.militaryActionCalculationDelegate.filter(d => d.Temporary));
    GetUrbanBuildingLimit = () => this.SumDelegate(this.urbanBuildingLimitCalculationDelegate);
    // Hand limits: the civil / military action totals plus any extra from cards
    // (Hammurabi's possible extra civil action does not raise the hand limit)
    GetCivilHandLimit = () => this.SumDelegate(this.civilActionCalculationDelegate.filter(d => !d.MilitaryAsCivil && !d.Restricted))
        + this.SumDelegate(this.civilHandLimitCalculationDelegate);
    GetMilitaryHandLimit = () => this.GetMilitaryActionCount() + this.SumDelegate(this.militaryHandLimitCalculationDelegate);

    // Tokens owned: the starting tokens, those from cards in play, and stored changes (events and the like)
    // (Cards can take tokens away, e.g. Vast Territory's -1 blue, so these keep their sign rather than stopping at 0)
    GetBlueTokens = () => StartingTokens.blue + this.blueTokenCalculationDelegate.reduce((sum, d) => sum + d.Action(), 0)
        + this.tokenChanges.blue.reduce((sum, change) => sum + change.amount, 0);
    GetYellowTokens = () => StartingTokens.yellow + this.yellowTokenCalculationDelegate.reduce((sum, d) => sum + d.Action(), 0)
        + this.tokenChanges.yellow.reduce((sum, change) => sum + change.amount, 0);

    // The banks hold whatever owned tokens are not in use: blue ones on farms and mines or covering wonder stages,
    // yellow ones as workers (unused or on cards). Moving a token onto a card takes it from the bank by itself.
    // The bank can fall below 0 for a moment (a token lost or used while it is empty); the player then chooses the farm or
    // mine it comes from (see Decisions SettleBlueBank, run after every move).
    get blueBank()
    {
        const onCards = this.technologies.reduce((sum, tech) => sum + tech.GetBlueCubeCount(), 0);
        return this.GetBlueTokens() - onCards - this.TokensOnWonders() - this.TokensKeptOnWonders();
    }
// Blue tokens covering the stages of the player's own wonder under way: every stage, whoever built it (a stage of an
    // International Red Cross built by another player is still covered by its owner's token)
    TokensOnWonders()
    {
        return this.wonderInProgress?.stagesBuilt ?? 0;
    }

    get yellowBank()
    {
        const onCards = this.technologies.reduce((sum, tech) => sum + tech.GetWorkerCount(), 0);
        return this.GetYellowTokens() - this.workerPool - onCards;
    }

    // Records tokens gained (amount > 0) or lost from somewhere other than a card in play, e.g. an event.
    // A blue token lost with the blue bank empty comes off a farm or mine of the player's choice (see Decisions
    // SettleBlueBank); yellow tokens are only ever taken from the yellow bank, as many as are there (the callers see to it).
    ChangeTokens(color, amount, source)
    {
        this.tokenChanges[color].push({ source, amount });
    }

    // Where the tokens owned come from
    TokenBreakdown(color, delegates)
    {
        const changes = new Map();
        this.tokenChanges[color].forEach(change => changes.set(change.source, (changes.get(change.source) ?? 0) + change.amount));
        return [{ source: "Starting tokens", value: StartingTokens[color], from: [] }, ...this.GetBreakdown(delegates),
            ...[...changes].map(([source, value]) => ({ source, value, from: [] })).filter(entry => entry.value !== 0)];
    }

    // Spends civil actions. Once the usual ones run out, Hammurabi's (a military action used as a civil action, shown as
    // an extra civil action while a military action is left) pays for one: the military action is spent, and the civil
    // action stays for the rest of the turn as a bonus credited to him (so it stays even if he is replaced).
    // context: what the actions are for (see Effects.AddRestrictedCivilAction): a civil action for that only (Gutenberg's)
    // pays first, if not used yet this turn
    SpendCivilActions(count = 1, context = null)
    {
        const restricted = this.RestrictedFor(context);
        if (restricted && count > 0)
        {
            this.turnFlags.push(restricted.flag);
            LogTrigger(this, restricted.Source?.name, restricted.onUse?.(this));
            count--;
        }
        const usualLeft = this.civilActionCalculationDelegate.filter(d => !d.MilitaryAsCivil && !d.Restricted).reduce((sum, d) => sum + d.Action(), 0)
            - this.civilActionsUsed;
        const extra = this.civilActionCalculationDelegate.find(d => d.MilitaryAsCivil && d.Action() > 0);
        this.civilActionsUsed += count;
        if (count > usualLeft && extra)
        {
            this.UseMilitaryActionAsCivil(extra.Source.name, true);
        }
    }

    // Hammurabi's ability, once per turn: a military action is spent and the player keeps a civil action for the turn.
    // special: the civil action is his (shown apart, in parentheses); not so for the one returned on replacing him.
    UseMilitaryActionAsCivil(source, special = false)
    {
        this.militaryActionsUsed++;
        this.turnFlags.push(TurnFlags.MilitaryAsCivil);
        this.turnBonuses.push({ source, stat: 'civilActions', amount: 1, ...(special ? { militaryAsCivil: true } : {}) });
    }

    // How many of the civil actions are Hammurabi's (a military action used as a civil action): offered, or used
    GetMilitaryAsCivilActions = () =>
        this.SumDelegate(this.civilActionCalculationDelegate.filter(d => d.MilitaryAsCivil))
        + this.turnBonuses.filter(bonus => bonus.militaryAsCivil).length;

    // The armies the military units form for the current tactic: { full, obsolete, doubled, value }, or null
    GetTacticArmies()
    {
        const tactic = this.currentTactic && TacticConverter(this.currentTactic);
        if (!tactic)
        {
            return null;
        }
        // (Jan Žižka: farms count as Age A infantry or artillery)
        const farms = HasRuleException(this, Rules.FarmsAsUnits)
            ? this.technologies.filter(tech => tech.type === TechnologyTypes.Production.Farm).reduce((sum, tech) => sum + tech.GetWorkerCount(), 0) : 0;
        // (Marlene Dietrich: one unit counts twice)
        const doubleOne = HasRuleException(this, Rules.DoubleOneUnit);
        const armies = TacticArmies(this, tactic, { infantryAsCavalry: HasRuleException(this, Rules.InfantryAsCavalry), farms, doubleOne });
        // Each army is worth more on a tactic card Sun Tzu went under (that card only)
        const bonus = this.TacticBonus();
        const withBonus = bonus > 0 ? { ...armies, bonus, value: armies.value + bonus * (armies.full + armies.obsolete) } : armies;
// What units the cards add stand in for (Jan Žižka's farms, Marlene Dietrich's unit counted twice): extraValue, and
        // extraBy / extraText for the hover ("Jan Žižka (2 farms)", "Marlene Dietrich (Riflemen counted twice)")
        if (armies.farms?.used > 0 || armies.counted)
        {
            const without = TacticArmies(this, tactic, { infantryAsCavalry: HasRuleException(this, Rules.InfantryAsCavalry) });
            withBonus.extraValue = withBonus.value - (without.value + bonus * (without.full + without.obsolete));
            const rule = armies.counted ? Rules.DoubleOneUnit : Rules.FarmsAsUnits;
            withBonus.extraBy = this.ruleExceptionDelegate.find(exception => exception.rule === rule)?.Source?.name;
            withBonus.extraText = armies.counted ? `${armies.counted} counted twice` : `${armies.farms.used} farm${armies.farms.used === 1 ? '' : 's'}`;
        }
        return withBonus;
    }

    // Strength per army that the player's tactic card has gained (Sun Tzu), or 0
    TacticBonus()
    {
        return this.currentTacticId !== null ? (this.gameState?.tacticBonuses?.[this.currentTacticId] ?? 0) : 0;
    }

// The usual civil actions left, plus, for an action one of them may pay for (context, see SpendCivilActions), a civil
    // action for some actions only (Gutenberg's)
    GetCivilActionsRemaining = (context = null) => this.GetCivilActionCount() - this.civilActionsUsed + (this.RestrictedFor(context) ? 1 : 0);

    // Civil actions for some actions only, for the board: [{ source, purpose, used }]
    GetRestrictedCivilActions = () => this.civilActionCalculationDelegate.filter(d => d.Restricted)
        .map(d => ({ source: d.Source?.name, purpose: d.purpose, used: this.turnFlags.includes(d.flag) }));

    // The unused civil action for some actions only that may pay for this action, if any
    // (not when the player chose not to use it for this move: restrictedDeclined, see Rules.MarkDeclined)
    RestrictedFor = (context) => context && !this.restrictedDeclined
        ? this.civilActionCalculationDelegate.find(d => d.Restricted && !this.turnFlags.includes(d.flag) && d.eligible(context))
        : undefined;
    GetMilitaryActionsRemaining = () => this.GetMilitaryActionCount() + this.GetTemporaryMilitaryActions() - this.militaryActionsUsed;

    GetBreakdowns = () => ({
        culture: this.GetBreakdown(this.cultureProductionCalculationDelegate),
        science: this.GetBreakdown(this.scienceProductionCalculationDelegate),
        // (less what the workers eat)
        food: [...this.GetBreakdown(this.foodProductionCalculationDelegate),
            ...(GetConsumption(this.yellowBank) > 0 ? [{ source: 'Consumption', value: -GetConsumption(this.yellowBank), from: [] }] : [])],
        resources: this.GetBreakdown(this.resourceProductionCalculationDelegate),
        military: this.GetBreakdown(this.militaryCalculationDelegate),
        happyFaces: this.GetBreakdown(this.happyFaceCalculationDelegate),
        colonization: this.GetBreakdown(this.colonizationBonusCalculationDelegate),
        civilActions: this.GetBreakdown(this.civilActionCalculationDelegate),
        militaryActions: this.GetBreakdown(this.militaryActionCalculationDelegate),
        blueTokens: this.TokenBreakdown('blue', this.blueTokenCalculationDelegate),
        yellowTokens: this.TokenBreakdown('yellow', this.yellowTokenCalculationDelegate),
    });

    GainFood = (amount) => this.GainTokens(TechnologyTypes.Production.Farm, amount);
    GainResources = (amount) => this.GainTokens(TechnologyTypes.Production.Mine, amount);

    // The cards blue tokens of this kind go on and come off: farms, or mines (and, while Bill Gates makes them mines, labs
    // of Age I or higher; tokens already on a lab can always be spent), highest value first. forGaining: cards a token
    // may be put on.
    TokenStores(techType, forGaining) {
        const labsAsMines = techType === TechnologyTypes.Production.Mine && HasRuleException(this, Rules.LabsAsMines);
        return this.technologies
            .filter(tech => tech.type === techType || (techType === TechnologyTypes.Production.Mine && tech.type === TechnologyTypes.Urban.Lab
                && tech.resourceValue > 0 && (forGaining ? labsAsMines : labsAsMines || tech.GetBlueCubeCount() > 0)))
            .sort((a, b) => TokenValue(b) - TokenValue(a));
    }

    // Moves blue tokens from the blue bank onto farms/mines worth `amount`, using the highest-value cards first.
    // Limited by the tokens left in the blue bank.
    GainTokens(techType, amount) {
        var remaining = amount;
        this.TokenStores(techType, true)
            .forEach(tech => {
                while (remaining >= TokenValue(tech) && this.blueBank > 0)
                {
                    tech.AddContent("blueCubes", 1);
                    remaining -= TokenValue(tech);
                }
            });
    }

    PayFood = (amount) => this.PayTokens(TechnologyTypes.Production.Farm, amount);
    PayResources = (amount) => this.PayTokens(TechnologyTypes.Production.Mine, amount);

    // Lose up to `amount`; returns how much could not be covered
    LoseFood = (amount) => this.LoseTokens(TechnologyTypes.Production.Farm, this.GetFood(), amount);
    LoseResources = (amount) => this.LoseTokens(TechnologyTypes.Production.Mine, this.GetResources(), amount);

    LoseTokens(techType, available, amount) {
        if (available >= amount)
        {
            this.PayTokens(techType, amount);
            return 0;
        }
        this.PayTokens(techType, available);
        return amount - available;
    }

    // Removes blue tokens worth `amount` from farms/mines, making change if needed. The tokens are chosen to put as many
    // back in the blue bank as possible (corruption counts the empty spaces there): the lowest-value tokens first, and
    // a bigger one broken only when that leaves more tokens in the bank.
    // Callers must check the player can afford it first.
    PayTokens(techType, amount) {
        if (amount <= 0)
        {
            return;
        }
        const techs = this.TokenStores(techType, false);
        const stores = this.TokenStores(techType, true);

        // The change for `value`, as tokens put on the highest-value cards first: { count, lost } (lost: what no card
        // could take)
        const change = (value) => stores.reduce((acc, tech) => {
            const n = Math.floor(acc.lost / TokenValue(tech));
            return { count: acc.count + n, lost: acc.lost - n * TokenValue(tech) };
        }, { count: 0, lost: value });

        // Every way of taking tokens off the cards whose total first reaches `amount`: total → { count, taken[] }
        // (the most tokens for each total, lowest values tried first)
        let ways = new Map([[0, { count: 0, taken: techs.map(() => 0) }]]);
        [...techs.keys()].reverse().forEach(i => {
            const next = new Map(ways);
            ways.forEach((way, total) => {
                for (let n = 1; n <= techs[i].GetBlueCubeCount() && total + (n - 1) * TokenValue(techs[i]) < amount; n++)
                {
                    const sum = total + n * TokenValue(techs[i]);
                    if (!next.has(sum) || next.get(sum).count < way.count + n)
                    {
                        next.set(sum, { count: way.count + n, taken: way.taken.map((t, j) => j === i ? n : t) });
                    }
                }
            });
            ways = next;
        });

        // The way that leaves the most tokens in the bank (losing no value if possible, then overpaying least)
        const best = [...ways].filter(([total]) => total >= amount)
            .map(([total, way]) => ({ total, way, change: change(total - amount) }))
            .sort((a, b) => a.change.lost - b.change.lost || (b.way.count - b.change.count) - (a.way.count - a.change.count) || a.total - b.total)[0];
        if (best === undefined)
        {
            throw new Error(`Cannot pay ${amount} from ${techType} tokens`);
        }
        techs.forEach((tech, i) => tech.AddContent("blueCubes", -best.way.taken[i]));

        // (the change goes back on the cards tokens may be put on)
        let owed = best.total - amount;
        stores.forEach(tech => {
            while (owed >= TokenValue(tech) && this.blueBank > 0)
            {
                tech.AddContent("blueCubes", 1);
                owed -= TokenValue(tech);
            }
        });
    }
}

// What one blue token on a card is worth: a farm's or mine's printed value, a lab's level (used as a mine: Bill Gates)
const TokenValue = (tech) => tech.type === TechnologyTypes.Urban.Lab ? tech.resourceValue : tech.multiplier;

module.exports = PlayerState;